import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, BarChart3, Activity, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';

function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState('trends');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [period, setPeriod] = useState('month');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  function fetchDashboard() {
    setLoading(true);
    var today = new Date();
    var startDate, endDate;
    if (period === 'week') {
      var weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      startDate = weekAgo.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else if (period === 'month') {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else {
      startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    }
    api.get('/analytics/dashboard', { params: { start_date: startDate, end_date: endDate, status: 'APPROVED,REJECTED' } })
      .then(function(res) { setDashboardData(res.data); })
      .catch(function() { toast.error('Failed to load dashboard'); })
      .finally(function() { setLoading(false); });
  }

  function fetchMonthlyReport() {
     api.get('/analytics/monthly-report', { params: { year: year, month: month, status: 'APPROVED,REJECTED' } })
      .then(function(res) { setMonthlyReport(res.data); })
      .catch(function() { toast.error('Failed to load report'); });
  }

  useEffect(function() { fetchDashboard(); }, [period]);
  useEffect(function() { if (activeTab === 'reports') fetchMonthlyReport(); }, [activeTab, year, month]);

  function exportReport() {
    toast.info('Use Export Data page for CSV exports');
  }

  var prodTrend = dashboardData && dashboardData.production_trend ? dashboardData.production_trend : [];
  var machPerf = dashboardData && dashboardData.machine_performance ? dashboardData.machine_performance : [];
  var oeeByMachine = dashboardData && dashboardData.oee_by_machine ? dashboardData.oee_by_machine : [];
  var oeeTrend = dashboardData && dashboardData.oee_trend ? dashboardData.oee_trend : [];
  var rejectionByReason = dashboardData && dashboardData.rejection_by_reason ? dashboardData.rejection_by_reason : [];
  var downtimeByReason = dashboardData && dashboardData.downtime_by_reason ? dashboardData.downtime_by_reason : [];

  return React.createElement('div', { className: 'space-y-6 animate-fade-in', 'data-testid': 'analytics-page' },
    // Header
    React.createElement('div', { className: 'flex flex-col md:flex-row md:items-center justify-between gap-4' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'font-heading text-3xl font-bold text-slate-900' }, 'Analytics'),
        React.createElement('p', { className: 'text-slate-500' }, 'Production trends, OEE analysis, and reports')
      ),
      React.createElement('div', { className: 'flex gap-3' },
        React.createElement(Button, { variant: 'outline', onClick: function() { fetchDashboard(); fetchMonthlyReport(); }, 'data-testid': 'refresh-analytics-btn' },
          React.createElement(RefreshCw, { className: 'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '') }), 'Refresh'
        ),
        React.createElement(Button, { variant: 'outline', onClick: exportReport, 'data-testid': 'export-btn' },
          React.createElement(Download, { className: 'w-4 h-4 mr-2' }), 'Export'
        )
      )
    ),
    
    // Tabs
    React.createElement(Tabs, { value: activeTab, onValueChange: setActiveTab },
      React.createElement(TabsList, { className: 'grid w-full max-w-md grid-cols-2' },
        React.createElement(TabsTrigger, { value: 'trends', 'data-testid': 'tab-trends' },
          React.createElement(TrendingUp, { className: 'w-4 h-4 mr-2' }), 'Trends & OEE'
        ),
        React.createElement(TabsTrigger, { value: 'reports', 'data-testid': 'tab-reports' },
          React.createElement(BarChart3, { className: 'w-4 h-4 mr-2' }), 'Monthly Reports'
        )
      ),
      
      // Trends Tab
      React.createElement(TabsContent, { value: 'trends', className: 'space-y-6 mt-6' },
        // Period selector
        React.createElement('div', { className: 'flex items-center gap-4' },
          React.createElement(Label, null, 'Period:'),
          React.createElement(Select, { value: period, onValueChange: setPeriod },
            React.createElement(SelectTrigger, { className: 'w-40', 'data-testid': 'trends-period-select' },
              React.createElement(SelectValue, null)
            ),
            React.createElement(SelectContent, null,
              React.createElement(SelectItem, { value: 'week' }, 'This Week'),
              React.createElement(SelectItem, { value: 'month' }, 'This Month'),
              React.createElement(SelectItem, { value: 'year' }, 'This Year')
            )
          )
        ),
        
        // Production Trend Chart
        React.createElement(Card, { className: 'border-slate-200' },
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, { className: 'flex items-center gap-2' },
              React.createElement(TrendingUp, { className: 'w-5 h-5 text-primary' }), 'Production Trend'
            )
          ),
          React.createElement(CardContent, null,
            React.createElement('div', { className: 'h-80' },
              prodTrend.length > 0 ?
              React.createElement(ResponsiveContainer, { width: '100%', height: '100%' },
                React.createElement(LineChart, { data: prodTrend },
                  React.createElement(CartesianGrid, { strokeDasharray: '3 3', stroke: '#E2E8F0' }),
                  React.createElement(XAxis, { dataKey: 'date', tick: { fontSize: 12 }, tickFormatter: function(v) { return v.slice(5); } }),
                  React.createElement(YAxis, { tick: { fontSize: 12 } }),
                  React.createElement(Tooltip, { contentStyle: { backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px', fontFamily: 'JetBrains Mono' } }),
                  React.createElement(Legend, null),
                  React.createElement(Line, { type: 'monotone', dataKey: 'produced', stroke: '#0052CC', strokeWidth: 2, dot: { fill: '#0052CC' }, name: 'Produced' }),
                  React.createElement(Line, { type: 'monotone', dataKey: 'target', stroke: '#00875A', strokeWidth: 2, strokeDasharray: '5 5', dot: false, name: 'Target' }),
                  React.createElement(Line, { type: 'monotone', dataKey: 'rejected', stroke: '#DE350B', strokeWidth: 2, dot: { fill: '#DE350B' }, name: 'Rejected' })
                )
              ) :
              React.createElement('div', { className: 'h-full flex items-center justify-center text-slate-400' },
                React.createElement('p', null, 'No production data for selected period')
              )
            )
          )
        ),
        
        // Two column layout for Machine Performance and OEE
        React.createElement('div', { className: 'grid grid-cols-1 lg:grid-cols-2 gap-6' },
          // Machine Performance Chart
          React.createElement(Card, { className: 'border-slate-200' },
            React.createElement(CardHeader, null,
              React.createElement(CardTitle, { className: 'flex items-center gap-2' },
                React.createElement(Activity, { className: 'w-5 h-5 text-primary' }), 'Machine Performance'
              )
            ),
            React.createElement(CardContent, null,
              React.createElement('div', { className: 'h-72' },
                machPerf.length > 0 ?
                React.createElement(ResponsiveContainer, { width: '100%', height: '100%' },
                  React.createElement(BarChart, { data: machPerf, layout: 'vertical' },
                    React.createElement(CartesianGrid, { strokeDasharray: '3 3', stroke: '#E2E8F0' }),
                    React.createElement(XAxis, { type: 'number', tick: { fontSize: 11 } }),
                    React.createElement(YAxis, { type: 'category', dataKey: 'machine', tick: { fontSize: 10 }, width: 60 }),
                    React.createElement(Tooltip, { contentStyle: { fontFamily: 'JetBrains Mono', fontSize: 12 } }),
                    React.createElement(Legend, null),
                    React.createElement(Bar, { dataKey: 'produced', fill: '#0052CC', name: 'Produced', radius: [0, 4, 4, 0] }),
                    React.createElement(Bar, { dataKey: 'rejected', fill: '#DE350B', name: 'Rejected', radius: [0, 4, 4, 0] })
                  )
                ) :
                React.createElement('div', { className: 'h-full flex items-center justify-center text-slate-400' },
                  React.createElement('p', null, 'No machine data')
                )
              )
            )
          ),
          
          // OEE by Machine Chart
          React.createElement(Card, { className: 'border-slate-200' },
            React.createElement(CardHeader, null,
              React.createElement(CardTitle, { className: 'flex items-center gap-2' },
                React.createElement(BarChart3, { className: 'w-5 h-5 text-primary' }), 'OEE by Machine'
              )
            ),
            React.createElement(CardContent, null,
              React.createElement('div', { className: 'h-72' },
                oeeByMachine.length > 0 ?
                React.createElement(ResponsiveContainer, { width: '100%', height: '100%' },
                  React.createElement(BarChart, { data: oeeByMachine, layout: 'vertical' },
                    React.createElement(CartesianGrid, { strokeDasharray: '3 3', stroke: '#E2E8F0' }),
                    React.createElement(XAxis, { type: 'number', domain: [0, 100], tick: { fontSize: 11 } }),
                    React.createElement(YAxis, { type: 'category', dataKey: 'machine', tick: { fontSize: 10 }, width: 60 }),
                    React.createElement(Tooltip, { contentStyle: { fontFamily: 'JetBrains Mono', fontSize: 12 }, formatter: function(v) { return v + '%'; } }),
                    React.createElement(Legend, null),
                    React.createElement(Bar, { dataKey: 'oee', fill: '#00875A', name: 'OEE %', radius: [0, 4, 4, 0] })
                  )
                ) :
                React.createElement('div', { className: 'h-full flex items-center justify-center text-slate-400' },
                  React.createElement('p', null, 'No OEE data')
                )
              )
            )
          )
        ),
        
        // OEE Trend Chart
        React.createElement(Card, { className: 'border-slate-200' },
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, { className: 'flex items-center gap-2' },
              React.createElement(Activity, { className: 'w-5 h-5 text-primary' }), 'OEE Trend'
            ),
            React.createElement(CardDescription, null, 'Availability × Performance × Quality')
          ),
          React.createElement(CardContent, null,
            React.createElement('div', { className: 'h-72' },
              oeeTrend.length > 0 ?
              React.createElement(ResponsiveContainer, { width: '100%', height: '100%' },
                React.createElement(LineChart, { data: oeeTrend },
                  React.createElement(CartesianGrid, { strokeDasharray: '3 3', stroke: '#E2E8F0' }),
                  React.createElement(XAxis, { dataKey: 'date', tick: { fontSize: 11 }, tickFormatter: function(v) { return v.slice(5); } }),
                  React.createElement(YAxis, { domain: [0, 100], tick: { fontSize: 11 } }),
                  React.createElement(Tooltip, { contentStyle: { fontFamily: 'JetBrains Mono', fontSize: 12 }, formatter: function(v) { return v + '%'; } }),
                  React.createElement(Legend, null),
                  React.createElement(Line, { type: 'monotone', dataKey: 'oee', stroke: '#00875A', strokeWidth: 2, dot: { fill: '#00875A' }, name: 'OEE' }),
                  React.createElement(Line, { type: 'monotone', dataKey: 'availability', stroke: '#0052CC', strokeWidth: 1, dot: false, name: 'Availability' }),
                  React.createElement(Line, { type: 'monotone', dataKey: 'performance', stroke: '#FFAB00', strokeWidth: 1, dot: false, name: 'Performance' }),
                  React.createElement(Line, { type: 'monotone', dataKey: 'quality', stroke: '#6554C0', strokeWidth: 1, dot: false, name: 'Quality' })
                )
              ) :
              React.createElement('div', { className: 'h-full flex items-center justify-center text-slate-400' },
                React.createElement('p', null, 'No OEE trend data')
              )
            )
          )
        ),
        
        // Rejection by Reason Chart
        React.createElement(Card, { className: 'border-slate-200' },
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, { className: 'flex items-center gap-2' },
              React.createElement(BarChart3, { className: 'w-5 h-5 text-primary' }), 'Rejection by Reason'
            ),
            React.createElement(CardDescription, null, 'Breakdown of rejected quantity by rejection reason code')
          ),
          React.createElement(CardContent, null,
            React.createElement('div', { className: 'h-72' },
              rejectionByReason.length > 0 ?
              React.createElement(ResponsiveContainer, { width: '100%', height: '100%' },
                React.createElement(BarChart, {
                  data: rejectionByReason,
                  layout: 'vertical',
                  margin: { top: 5, right: 20, left: 80, bottom: 5 }
                },
                  React.createElement(CartesianGrid, { strokeDasharray: '3 3', stroke: '#E2E8F0' }),
                  React.createElement(XAxis, { type: 'number', tick: { fontSize: 11 } }),
                  React.createElement(YAxis, {
                    type: 'category',
                    dataKey: 'reason_code',
                    tick: { fontSize: 11 },
                    width: 70,
                    tickFormatter: function(code) {
                      var item = rejectionByReason.find(function(r) { return r.reason_code === code; });
                      return item && item.reason_label && item.reason_label !== code ? code + ' - ' + item.reason_label : code;
                    }
                  }),
                  React.createElement(Tooltip, {
                    contentStyle: { fontFamily: 'JetBrains Mono', fontSize: 12 },
                    labelFormatter: function(code, payload) {
                      if (payload && payload[0] && payload[0].payload) {
                        var p = payload[0].payload;
                        return (p.reason_label && p.reason_label !== p.reason_code) ? p.reason_code + ' - ' + p.reason_label : p.reason_code;
                      }
                      return code;
                    }
                  }),
                  React.createElement(Bar, { dataKey: 'total_qty', fill: '#DE350B', name: 'Rejected', radius: [0, 4, 4, 0] })
                )
              ) :
              React.createElement('div', { className: 'h-full flex items-center justify-center text-slate-400' },
                React.createElement('p', null, 'No rejection breakdown data for selected period')
              )
            )
          )
        ),

        // Downtime by Reason Chart
        React.createElement(Card, { className: 'border-slate-200' },
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, { className: 'flex items-center gap-2' },
              React.createElement(BarChart3, { className: 'w-5 h-5 text-primary' }), 'Downtime by Reason'
            ),
            React.createElement(CardDescription, null, 'Breakdown of downtime (minutes) by reason code')
          ),
          React.createElement(CardContent, null,
            React.createElement('div', { className: 'h-72' },
              downtimeByReason.length > 0 ?
              React.createElement(ResponsiveContainer, { width: '100%', height: '100%' },
                React.createElement(BarChart, {
                  data: downtimeByReason,
                  layout: 'vertical',
                  margin: { top: 5, right: 20, left: 80, bottom: 5 }
                },
                  React.createElement(CartesianGrid, { strokeDasharray: '3 3', stroke: '#E2E8F0' }),
                  React.createElement(XAxis, { type: 'number', tick: { fontSize: 11 }, name: 'Minutes' }),
                  React.createElement(YAxis, {
                    type: 'category',
                    dataKey: 'reason_code',
                    tick: { fontSize: 11 },
                    width: 70,
                    tickFormatter: function(code) {
                      var item = downtimeByReason.find(function(r) { return r.reason_code === code; });
                      return item && item.reason_label && item.reason_label !== code ? code + ' - ' + item.reason_label : code;
                    }
                  }),
                  React.createElement(Tooltip, {
                    contentStyle: { fontFamily: 'JetBrains Mono', fontSize: 12 },
                    formatter: function(v) { return v + ' min'; },
                    labelFormatter: function(code, payload) {
                      if (payload && payload[0] && payload[0].payload) {
                        var p = payload[0].payload;
                        return (p.reason_label && p.reason_label !== p.reason_code) ? p.reason_code + ' - ' + p.reason_label : p.reason_code;
                      }
                      return code;
                    }
                  }),
                  React.createElement(Bar, { dataKey: 'total_minutes', fill: '#6554C0', name: 'Minutes', radius: [0, 4, 4, 0] })
                )
              ) :
              React.createElement('div', { className: 'h-full flex items-center justify-center text-slate-400' },
                React.createElement('p', null, 'No downtime breakdown data for selected period')
              )
            )
          )
        )
      ),
      
      // Reports Tab
      React.createElement(TabsContent, { value: 'reports', className: 'space-y-6 mt-6' },
        // Year/Month selector
        React.createElement('div', { className: 'flex items-center gap-4' },
          React.createElement(Label, null, 'Year:'),
          React.createElement(Input, { type: 'number', value: year, onChange: function(e) { setYear(parseInt(e.target.value)); }, className: 'w-24', 'data-testid': 'report-year' }),
          React.createElement(Label, null, 'Month:'),
          React.createElement(Select, { value: String(month), onValueChange: function(v) { setMonth(parseInt(v)); } },
            React.createElement(SelectTrigger, { className: 'w-36', 'data-testid': 'report-month' },
              React.createElement(SelectValue, null)
            ),
            React.createElement(SelectContent, null,
              [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(function(m) {
                return React.createElement(SelectItem, { key: m, value: String(m) },
                  new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })
                );
              })
            )
          ),
          React.createElement(Button, { variant: 'outline', onClick: fetchMonthlyReport, 'data-testid': 'generate-report-btn' }, 'Generate')
        ),
        
        // Monthly Report
        monthlyReport ?
        React.createElement(Card, { className: 'border-slate-200' },
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, null, 'Monthly Report: ' + monthlyReport.period),
            React.createElement(CardDescription, null, 'Production summary by machine')
          ),
          React.createElement(CardContent, null,
            // Summary cards
            React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-4 gap-6 mb-6' },
              React.createElement('div', { className: 'p-4 bg-slate-50 rounded-lg' },
                React.createElement('p', { className: 'text-xs text-slate-600 font-medium uppercase' }, 'Total Shifts'),
                React.createElement('p', { className: 'font-mono text-3xl font-bold text-slate-900' }, monthlyReport.total_runs)
              ),
              React.createElement('div', { className: 'p-4 bg-green-50 rounded-lg' },
                React.createElement('p', { className: 'text-xs text-green-600 font-medium uppercase' }, 'Total Produced'),
                React.createElement('p', { className: 'font-mono text-3xl font-bold text-green-900' },
                  monthlyReport.machine_summary.reduce(function(s, m) { return s + m.total_produced; }, 0).toLocaleString()
                )
              ),
              React.createElement('div', { className: 'p-4 bg-red-50 rounded-lg' },
                React.createElement('p', { className: 'text-xs text-red-600 font-medium uppercase' }, 'Total Rejected'),
                React.createElement('p', { className: 'font-mono text-3xl font-bold text-red-900' },
                  monthlyReport.machine_summary.reduce(function(s, m) { return s + m.total_rejected; }, 0).toLocaleString()
                )
              ),
              React.createElement('div', { className: 'p-4 bg-amber-50 rounded-lg' },
                React.createElement('p', { className: 'text-xs text-amber-600 font-medium uppercase' }, 'Total Downtime'),
                React.createElement('p', { className: 'font-mono text-3xl font-bold text-amber-900' },
                  monthlyReport.machine_summary.reduce(function(s, m) { return s + m.total_downtime; }, 0) + ' min'
                )
              )
            ),
            // Machine table
            React.createElement('div', { className: 'overflow-x-auto' },
              React.createElement('table', { className: 'w-full text-sm' },
                React.createElement('thead', null,
                  React.createElement('tr', { className: 'border-b bg-slate-50' },
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Machine'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Shifts'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Target'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Produced'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Rejected'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Rej %'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Downtime')
                  )
                ),
                React.createElement('tbody', null,
                  monthlyReport.machine_summary.map(function(m, i) {
                    return React.createElement('tr', { key: i, className: 'border-b hover:bg-slate-50' },
                      React.createElement('td', { className: 'p-3 font-mono font-medium' }, m.machine),
                      React.createElement('td', { className: 'p-3 text-right font-mono' }, m.shifts),
                      React.createElement('td', { className: 'p-3 text-right font-mono' }, m.total_target.toLocaleString()),
                      React.createElement('td', { className: 'p-3 text-right font-mono text-green-600 font-medium' }, m.total_produced.toLocaleString()),
                      React.createElement('td', { className: 'p-3 text-right font-mono text-red-600' }, m.total_rejected.toLocaleString()),
                      React.createElement('td', { className: 'p-3 text-right font-mono ' + (m.rejection_rate > 5 ? 'text-red-600 font-medium' : '') }, m.rejection_rate + '%'),
                      React.createElement('td', { className: 'p-3 text-right font-mono' }, m.total_downtime + ' min')
                    );
                  })
                )
              )
            )
          )
        ) :
        React.createElement(Card, { className: 'border-slate-200' },
          React.createElement(CardContent, { className: 'py-12 text-center' },
            React.createElement(BarChart3, { className: 'w-12 h-12 mx-auto mb-4 text-slate-300' }),
            React.createElement('h3', { className: 'font-medium text-slate-700 mb-2' }, 'No Report Data'),
            React.createElement('p', { className: 'text-slate-500' }, 'Select year and month, then click Generate')
          )
        )
      )
    )
    
    /* DISABLED: SPC/Inspection Tab - Can be re-enabled later when inspection logging is enabled
    React.createElement(TabsContent, { value: 'spc', className: 'space-y-6 mt-6' },
      // SPC Charts for inspection measurements
    )
    */
  );
}

export default AnalyticsPage;
